/**
 * ClenoveWorkspace — company-admin member & grants editor (access overhaul
 * Stage 5, `/management?tab=clenove`).
 *
 * One row per member of the active company, one compact level-select per
 * entitled module (DIRECT grants — 'none' means no direct grant; the server
 * still derives the zakaznici implication for the effective level), the
 * member's LMS locations (read-only: whose leads they see and take over;
 * shown only when the company has LMS — a platform admin sets the LMS ID and
 * locations in Platforma → Uživatelé), plus the Správce toggle. Every change PUTs immediately and re-fetches on success.
 * Every change is recorded in the company history — the Historie tab
 * (shared HistorieWorkspace, `historie_firma`).
 *
 * Server boundary: everything here is requireSpravce-gated — the UI gate
 * (`canManage`) only mirrors it for UX.
 */

import { Link } from '@tanstack/react-router';
import { EmptyState } from '@/components/EmptyState';
import { Checkbox } from '@/components/Checkbox';
import { Marker } from '@/components/Marker';
import { Skeleton } from '@/components/Skeleton';
import { toast } from '@/components/Toaster';
import { Toolband } from '@/components/Toolband';
import { ApiError } from '@/lib/api';
import {
  useAuthMeQuery,
  useFirmaMembersQuery,
  useUpdatePravaMutation,
  useUpdateSpravceMutation,
  type FirmaMember,
  type FirmaUroven,
} from './api';

interface ClenoveWorkspaceProps {
  /** Company admin (spravce) or platform admin — mirrors requireSpravce. */
  canManage: boolean;
}

/** Canonical column order + Czech labels (mirrors the module naming used across the app). */
const MODUL_META: { key: string; label: string }[] = [
  { key: 'kontakty', label: 'Kontakty' },
  { key: 'vozidla', label: 'Vozidla' },
  { key: 'ojeta_vozidla', label: 'Ojetá vozidla' },
  { key: 'leasing', label: 'Leasing' },
  { key: 'zakaznici', label: 'Zákazníci' },
  { key: 'servis', label: 'Servis' },
  { key: 'uskladneni', label: 'Uskladnění' },
  { key: 'posta', label: 'Pošta' },
  { key: 'kalendar', label: 'Kalendář' },
  { key: 'ukoly', label: 'Úkoly' },
  { key: 'pujcovna', label: 'Půjčovna' },
];

const MODUL_LABEL: Record<string, string> = Object.fromEntries(
  MODUL_META.map((m) => [m.key, m.label]),
);

/** zakaznici is grantable whenever any of these is entitled (server rule). */
const ZAKAZNICI_IMPLIED_BY = ['vozidla', 'ojeta_vozidla', 'leasing', 'pujcovna', 'servis', 'kontakty', 'uskladneni'];

const LEVELS: { value: FirmaUroven; label: string }[] = [
  { value: 'none', label: 'Žádný' },
  { value: 'read', label: 'Čtení' },
  { value: 'write', label: 'Zápis' },
  { value: 'admin', label: 'Správa' },
];

const RANK: Record<FirmaUroven, number> = { none: 0, read: 1, write: 2, admin: 3 };
const LEVEL_LABEL_LC: Record<FirmaUroven, string> = {
  none: 'žádný',
  read: 'čtení',
  write: 'zápis',
  admin: 'správa',
};

/**
 * Client-side mirror of the server's single implication rule
 * (api/src/services/access.ts): vozidla/ojetá/leasing/půjčovna carry their full level
 * to zakaznici; servis/kontakty/uskladněni at zápis+ imply čtení. Returns the strongest
 * implied source so the editor can show WHY a member has more than their
 * direct grant — the select edits the direct grant, but the max always wins,
 * so a spravce needs to see the computed value to avoid "Žádný that isn't".
 */
function impliedZakaznici(
  prava: Record<string, FirmaUroven | undefined>,
): { uroven: FirmaUroven; zdroj: string } | null {
  let best: { uroven: FirmaUroven; zdroj: string } | null = null;
  for (const key of ['vozidla', 'ojeta_vozidla', 'leasing', 'pujcovna'] as const) {
    const lvl = prava[key] ?? 'none';
    if (lvl !== 'none' && (!best || RANK[lvl] > RANK[best.uroven])) {
      best = { uroven: lvl, zdroj: key };
    }
  }
  for (const key of ['servis', 'kontakty', 'uskladneni'] as const) {
    if (RANK[prava[key] ?? 'none'] >= RANK.write && (!best || RANK.read > RANK[best.uroven])) {
      best = { uroven: 'read', zdroj: key };
    }
  }
  return best;
}

function memberName(m: FirmaMember): string {
  const full = `${m.prijmeni} ${m.jmeno}`.trim();
  return full || m.nick;
}

export function ClenoveWorkspace({ canManage }: ClenoveWorkspaceProps) {
  const me = useAuthMeQuery();
  const members = useFirmaMembersQuery(canManage);
  const updatePrava = useUpdatePravaMutation();
  const updateSpravce = useUpdateSpravceMutation();

  if (!canManage) {
    return (
      <div className="flex min-h-0 flex-1 flex-col px-8 pb-8 pt-5">
        <EmptyState
          eyebrow="403"
          title="Pouze pro správce firmy"
          description="Členy a jejich práva spravují správci firmy a administrátoři."
        />
      </div>
    );
  }

  const myId = me.data?.user.id;
  const rows = members.data?.members ?? [];
  const entitled = new Set(members.data?.moduly ?? []);
  if (ZAKAZNICI_IMPLIED_BY.some((k) => entitled.has(k))) entitled.add('zakaznici');
  // Canonical order first, then any server-sent module we don't know yet.
  const columns = [
    ...MODUL_META.filter((m) => entitled.has(m.key)),
    ...[...entitled]
      .filter((k) => !MODUL_LABEL[k])
      .sort()
      .map((k) => ({ key: k, label: k })),
  ];

  const accessDenied =
    members.isError && members.error instanceof ApiError && members.error.status === 403;

  const onLevelChange = (member: FirmaMember, modul: string, next: FirmaUroven) => {
    updatePrava.mutate(
      { userId: member.id_uzivatele, prava: { [modul]: next } },
      {
        onError: (err) =>
          toast.error(
            err instanceof ApiError ? err.message : 'Změnu práva se nepodařilo uložit',
          ),
      },
    );
  };

  const lmsPobocky = members.data?.lms_pobocky ?? [];

  const onSpravceChange = (member: FirmaMember, next: boolean) => {
    updateSpravce.mutate(
      { userId: member.id_uzivatele, spravce: next ? 1 : 0 },
      {
        onError: (err) =>
          toast.error(
            err instanceof ApiError && err.status === 409
              ? 'Vlastní příznak správce si odebrat nemůžete.'
              : err instanceof ApiError
                ? err.message
                : 'Změnu správce se nepodařilo uložit',
          ),
      },
    );
  };

  return (
    <div data-testid="management-clenove" className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <Toolband>
        <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-ink-3">
          Členové firmy · {rows.length} {rows.length === 1 ? 'člen' : rows.length < 5 && rows.length > 0 ? 'členové' : 'členů'}
        </span>
        <Toolband.Spacer />
        <span className="hidden font-mono text-[10.5px] text-ink-3 sm:inline">
          Přímá práva — změny se ukládají okamžitě
        </span>
      </Toolband>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto px-8 pb-8 pt-5">

      {members.isLoading ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      ) : accessDenied ? (
        <EmptyState
          eyebrow="403"
          title="Přístup odepřen"
          description="Členy a jejich práva spravují správci firmy a administrátoři."
        />
      ) : members.isError ? (
        <EmptyState eyebrow="Chyba" title="Členy nelze načíst" description="Zkuste obnovit stránku." />
      ) : rows.length === 0 ? (
        <EmptyState
          title="Firma zatím nemá žádné členy"
          description="Členy do firmy přidává administrátor v sekci Firmy."
        />
      ) : (
        <div className="overflow-x-auto border border-line bg-paper">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="border-b border-line bg-surface-2 text-left font-mono text-[10.5px] uppercase tracking-[0.06em] text-ink-3">
                <th className="px-3 py-2 font-medium">Člen</th>
                {columns.map((c) => (
                  <th key={c.key} className="px-3 py-2 font-medium">
                    {c.label}
                  </th>
                ))}
                {lmsPobocky.length > 0 && (
                  <th
                    className="px-3 py-2 font-medium"
                    title="Pobočky LMS, jejichž leady člen vidí a přebírá. Nastavuje administrátor platformy."
                  >
                    LMS pobočky
                  </th>
                )}
                <th className="px-3 py-2 text-center font-medium">Správce</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => {
                const rowBusy =
                  (updatePrava.isPending &&
                    updatePrava.variables?.userId === m.id_uzivatele) ||
                  (updateSpravce.isPending &&
                    updateSpravce.variables?.userId === m.id_uzivatele);
                const isSelf = myId != null && m.id_uzivatele === myId;
                return (
                  <tr key={m.id_uzivatele} className="border-b border-line align-top last:border-0">
                    <td className="whitespace-nowrap px-3 py-2.5">
                      <div className="flex items-center gap-2 text-ink">
                        {memberName(m)}
                        {m.aktivni === 0 && <Marker tone="danger">Neaktivní</Marker>}
                      </div>
                      <div className="font-mono text-[11px] text-ink-3">
                        {m.nick}
                        {m.email ? ` · ${m.email}` : ''}
                      </div>
                    </td>
                    {columns.map((c) => {
                      const direct = m.prava[c.key] ?? 'none';
                      // Zákazníci is the one implied module: show the computed
                      // effective level whenever an implication outranks the
                      // direct grant (the select alone would read "Žádný"
                      // while the member effectively has access).
                      const implied = c.key === 'zakaznici' ? impliedZakaznici(m.prava) : null;
                      const hint = implied && RANK[implied.uroven] > RANK[direct] ? implied : null;
                      return (
                        <td key={c.key} className="whitespace-nowrap px-3 py-2.5">
                          <select
                            aria-label={`${memberName(m)} — ${c.label}`}
                            value={direct}
                            disabled={rowBusy}
                            onChange={(e) =>
                              onLevelChange(m, c.key, e.target.value as FirmaUroven)
                            }
                            className="border border-line bg-paper px-1.5 py-1 font-mono text-[11px] text-ink focus:border-accent focus:outline-none disabled:opacity-50"
                          >
                            {LEVELS.map((l) => (
                              <option key={l.value} value={l.value}>
                                {l.label}
                              </option>
                            ))}
                          </select>
                          {hint && (
                            <div
                              className="mt-0.5 font-mono text-[10px] text-ink-3"
                              title={`Efektivní úroveň zděděná z modulu ${MODUL_LABEL[hint.zdroj] ?? hint.zdroj} — přímé právo ji nemůže snížit.`}
                            >
                              → {LEVEL_LABEL_LC[hint.uroven]} ({MODUL_LABEL[hint.zdroj] ?? hint.zdroj})
                            </div>
                          )}
                        </td>
                      );
                    })}
                    {lmsPobocky.length > 0 && (
                      <td className="whitespace-nowrap px-3 py-2.5" data-testid="clenove-lms-pobocky">
                        {/* Read-only: LMS settings belong to the platform admin. */}
                        {m.lms_id > 0 ? (
                          <span
                            className="font-mono text-[11px] text-ink-2"
                            title={`LMS ID ${m.lms_id} · pobočky nastavuje administrátor platformy.`}
                          >
                            {lmsPobocky
                              .filter((p) => m.lms_pobocky.includes(p.id))
                              .map((p) => p.nazev || `LMS #${p.id}`)
                              .join(', ') || '—'}
                          </span>
                        ) : (
                          <span
                            className="font-mono text-[11px] text-ink-3"
                            title="Člen není prodejcem v LMS. LMS ID a pobočky nastavuje administrátor platformy."
                          >
                            Bez LMS ID
                          </span>
                        )}
                      </td>
                    )}
                    <td
                      className="px-3 py-2.5 text-center"
                      title={
                        isSelf
                          ? 'Vlastní příznak správce si odebrat nemůžete — o změnu požádejte jiného správce.'
                          : undefined
                      }
                    >
                      <Checkbox
                        aria-label={`Správce — ${memberName(m)}`}
                        checked={m.spravce === 1}
                        disabled={rowBusy || isSelf}
                        onChange={(e) => onSpravceChange(m, e.target.checked)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p data-testid="clenove-historie-link" className="text-[12.5px] text-ink-3">
        Změny práv, správců, poboček LMS i účtů najdete v záložce{' '}
        <Link to="/management" search={{ tab: 'historie' }} className="text-accent-text hover:underline">
          Historie
        </Link>
        .
      </p>
      </div>
    </div>
  );
}
